/**
 * CHMED Document Manager
 * 
 * Handles CHMED 16A1 medication plans display and management
 * API: /apps/arztfx/listChmeds (Shopify App Proxy)
 */

class ChmedDocumentManager {

  /**
   * Hidden Fields auslesen
   */
  getHiddenFieldsAttributes() {
    const hiddenFields = document.querySelectorAll('#walterHiddenFields input');
    const attribs = {};
    Array.from(hiddenFields).forEach(input => {
      const match = input.name.match(/^attributes\[(.+)\]$/);
      const key = match ? match[1] : input.name;
      attribs[key] = input.value;
    });
    return attribs;
  }

  /**
   * Customer-ID ermitteln
   */
  getCustomerId() {
    return window.CHMED_CUSTOMER_ID || null;
  }

  /**
   * Default-Adresse ermitteln
   */
  getDefaultAddressId() {
    if (window.CHMED_DEFAULT_ADDRESS_ID) {
      return window.CHMED_DEFAULT_ADDRESS_ID;
    }
    // Fallback: erste Adresse mit gültigem id/ID
    if (window.CHMED_ADDRESSES && Array.isArray(window.CHMED_ADDRESSES) && window.CHMED_ADDRESSES.length > 0) {
      const valid = window.CHMED_ADDRESSES.find(a => a.id || a.ID);
      if (valid) {
        return valid.id || valid.ID;
      }
    }
  // ...
    return null;
  }
  constructor() {
    // Use Shopify App Proxy - relative path for multi-domain support
    this.apiBaseUrl = '/apps/arztfx';
    this.init();
  }

  /**
   * Initialize the manager
   */
  init() {
    // Setup retry button
    const retryBtn = document.getElementById('chmed-retry');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.loadDocuments());
    }

    // Setup upload functionality
    this.setupUpload();

    // Load documents on init
    this.loadDocuments();
  }

  /**
   * Setup upload area with file input and drag & drop
   */
  setupUpload() {
    // Empty state upload
    const uploadBtn = document.getElementById('chmed-upload-btn');
    const fileInput = document.getElementById('chmed-file-input');
    const dropzone = document.getElementById('chmed-dropzone');

    if (uploadBtn && fileInput) {
      uploadBtn.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) this.uploadFile(file);
      });
    }

    if (dropzone) {
      dropzone.addEventListener('click', () => fileInput?.click());
      
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('drag-over');
      });

      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('drag-over');
      });

      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('drag-over');
        
        const file = e.dataTransfer.files[0];
        if (file) this.uploadFile(file);
      });
    }

    // Toolbar upload with drag & drop (when documents are displayed)
    const uploadBtnToolbar = document.getElementById('chmed-upload-btn-toolbar');
    const fileInputToolbar = document.getElementById('chmed-file-input-toolbar');
    const dropzoneToolbar = document.getElementById('chmed-dropzone-toolbar');

    if (uploadBtnToolbar && fileInputToolbar) {
      uploadBtnToolbar.addEventListener('click', () => fileInputToolbar.click());
      fileInputToolbar.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) this.uploadFile(file);
      });
    }

    if (dropzoneToolbar && fileInputToolbar) {
      dropzoneToolbar.addEventListener('click', () => fileInputToolbar.click());
      
      dropzoneToolbar.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzoneToolbar.classList.add('drag-over');
      });

      dropzoneToolbar.addEventListener('dragleave', () => {
        dropzoneToolbar.classList.remove('drag-over');
      });

      dropzoneToolbar.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzoneToolbar.classList.remove('drag-over');
        
        const file = e.dataTransfer.files[0];
        if (file) this.uploadFile(file);
      });
    }
  }

  /**
   * Upload CHMED file to backend
   * @param {File} file - The file to upload (PDF or image)
   */
  async uploadFile(file) {
    // Validate file type - accept PDF and images
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');
    
    if (!isPdf && !isImage) {
      alert('Ungültiges Dateiformat. Bitte laden Sie eine PDF-Datei oder ein Bild (JPG, PNG, GIF, WebP) hoch.');
      return;
    }

    // Show loading
    const loading = document.getElementById('chmed-loading');
    const empty = document.getElementById('chmed-empty');
    empty.style.display = 'none';
    loading.style.display = 'block';

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      
      // API expects different field names: 'pdf' for PDFs, 'image' for images
      if (isPdf) {
        formData.append('pdf', file);
      } else {
        formData.append('image', file);
      }
      
      // Add metadata (Shopify App Proxy automatically adds logged_in_customer_id)
      formData.append('metadata', JSON.stringify({
        source: 'shopify-theme-upload'
      }));

      // Upload via App Proxy
      const response = await fetch('/apps/chmed', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload fehlgeschlagen: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Upload fehlgeschlagen');
      }

      // Reload documents list
      await this.loadDocuments();
      
      alert('✅ CHMED-Dokument erfolgreich hochgeladen und verarbeitet!');

    } catch (err) {
  // ...
      loading.style.display = 'none';
      empty.style.display = 'block';
      alert('Fehler beim Hochladen: ' + err.message);
    }
  }

  /**
   * Load CHMED documents from API
   */
  async loadDocuments() {
    const loading = document.getElementById('chmed-loading');
    const error = document.getElementById('chmed-error');
    const table = document.getElementById('chmed-table-wrapper');
    const empty = document.getElementById('chmed-empty');

    // Show loading, hide others
    loading.style.display = 'block';
    error.style.display = 'none';
    table.style.display = 'none';
    empty.style.display = 'none';

    try {
      // Call real API via Shopify App Proxy
      // Authentication handled automatically by Shopify (logged_in_customer_id)
      const response = await fetch(`${this.apiBaseUrl}/listChmeds`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Nicht autorisiert. Bitte erneut anmelden.');
        } else if (response.status === 404) {
          throw new Error('API-Endpunkt nicht gefunden. Bitte kontaktieren Sie den Support.');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'API-Fehler');
      }

      // Convert API response format to internal format
      const documents = (data.files || []).map(file => ({
        id: file.fileName,  // Use fileName as unique ID
        filename: file.fileName,
        created_at: file.uploadTimestamp || new Date().toISOString(),
        // API returns metadata.chmed as JSON string - parse it
        content: file.metadata?.chmed || null,
        downloadUrl: file.downloadUrl,
        metadata: file.metadata,
        variants: file.variants || [] // Include variants from API response
      }));

      this.renderDocuments(documents);
      
    } catch (err) {
  // ...
      this.showError(err.message || 'Fehler beim Laden der Dokumente');
    }
  }

  /**
   * Parse CHMED JSON content
   * @param {Object} document - Document object with content or url
   * @returns {Object|null} - Parsed CHMED data
   */
  parseChmedContent(document) {
    let chmedData;
    
    try {
      if (document.content) {
        // Content is string from metadata.chmed, parse as JSON
        chmedData = typeof document.content === 'string' 
          ? JSON.parse(document.content) 
          : document.content;
      } else {
        // No inline content available
        return null;
      }
    } catch (e) {
  // ...
      return null;
    }

    // Handle both CHMED formats - parsed format and raw format
    const patient = chmedData.patient || chmedData.Patient;
    const medications = chmedData.medications || chmedData.Medicaments;

    return {
      patient: {
        firstName: patient?.firstName || patient?.FName || '-',
        lastName: patient?.lastName || patient?.LName || '-',
        birthDate: patient?.birthDate || patient?.BDt || ''
      },
      doctor: {
        // Get from metadata or parsed structure
        name: document.metadata?.prescribingDoctors || 
              [chmedData.Hcp?.FName, chmedData.Hcp?.LName].filter(Boolean).join(' ') || 
              '-',
        gln: chmedData.Hcp?.GLN || ''
      },
      medications: this.parseMedications(medications || []),
      date: chmedData.timestamp || chmedData.MedHeader?.Dt || document.created_at,
      format: chmedData.format || document.metadata?.chmedFormat || 'CHMED'
    };
  }

  /**
   * Parse medications array
   * @param {Array} medicaments - CHMED medicaments (can be parsed or raw format)
   * @returns {Array}
   */
  parseMedications(medicaments) {
    if (!Array.isArray(medicaments)) return [];
    
    return medicaments.map(med => {
      // Handle both parsed format (medications[]) and raw format (Medicaments[])
      const id = med.id || med.Id;
      const idType = med.idType || med.IdType;
      const posology = med.posology || med.Pos;
      
      return {
        name: this.getMedicationName({ id, idType, Id: id, IdType: idType }),
        dosage: this.getPosology(posology)
      };
    });
  }

  /**
   * Get medication name based on IdType
   * @param {Object} med - Medication object
   * @returns {string}
   */
  getMedicationName(med) {
    const id = med.id || med.Id;
    const idType = med.idType || med.IdType;
    
    if (idType === 0) return id; // Free text
    if (idType === 1) return id; // Article name
    if (idType === 2) return `GTIN: ${id}`;
    if (idType === 3) return `Pharmacode: ${id}`;
    return id || 'Unbekannt';
  }

  /**
   * Get posology (dosage) string
   * @param {Array} posArray - Posology array (can be parsed or raw format)
   * @returns {string}
   */
  getPosology(posArray) {
    if (!posArray || !Array.isArray(posArray) || posArray.length === 0) return '';
    
    // Handle both parsed format and raw format
    const dosages = posArray
      .map(p => {
        // Get D field - can be array or object
        const d = p.D || p.d;
        if (Array.isArray(d)) {
          return d.join('-'); // e.g., [1,0,1,0] -> "1-0-1-0"
        }
        return d || p.DtFrom || '';
      })
      .filter(Boolean);
    
    return dosages.join(', ');
  }

  /**
   * Format date string
   * @param {string} dateStr - Date string (ISO or YYYY-MM-DD)
   * @returns {string}
   */
  formatDate(dateStr) {
    if (!dateStr) return '-';
    
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  }

  /**
   * Render documents to table
   * @param {Array} documents - Array of document objects
   */
  renderDocuments(documents) {
    const loading = document.getElementById('chmed-loading');
    const table = document.getElementById('chmed-table-wrapper');
    const empty = document.getElementById('chmed-empty');
    const tbody = document.getElementById('chmed-documents-body');

    loading.style.display = 'none';

    if (!documents || documents.length === 0) {
      empty.style.display = 'block';
      return;
    }

    // Filter out documents that can't be parsed
    const validDocuments = documents
      .map(doc => ({ doc, chmed: this.parseChmedContent(doc) }))
      .filter(item => item.chmed !== null);

    if (validDocuments.length === 0) {
      empty.style.display = 'block';
      return;
    }

    table.style.display = 'block';
    tbody.innerHTML = '';

    // Update document count in toolbar
    const countEl = document.getElementById('chmed-count');
    if (countEl) {
      const count = validDocuments.length;
      countEl.textContent = `${count} Dokument${count !== 1 ? 'e' : ''}`;
    }

    validDocuments.forEach(({ doc, chmed }) => {
      const row = this.createDocumentRow(doc, chmed);
      tbody.appendChild(row);

      // Add variants row if available (hidden by default)
      if (doc.variants && doc.variants.length > 0) {
        const variantsRow = document.createElement('tr');
        variantsRow.className = 'variants-row';
        variantsRow.dataset.documentId = doc.id;
        variantsRow.style.display = 'none'; // Hidden by default

        const variantsCell = document.createElement('td');
        variantsCell.setAttribute('colspan', '6');
        variantsCell.innerHTML = this.renderVariantsSection(doc.variants, doc.id);

        variantsRow.appendChild(variantsCell);
        tbody.appendChild(variantsRow);

        // Attach event listeners for action buttons
        const addToCartBtn = variantsCell.querySelector('.btn-add-to-cart');
        const orderNowBtn = variantsCell.querySelector('.btn-order-now');

        if (addToCartBtn) {
          addToCartBtn.addEventListener('click', () => {
            this.addVariantsToCart(doc.id);
          });
        }

        if (orderNowBtn) {
          orderNowBtn.addEventListener('click', () => {
            this.createOrderFromVariants(doc.id);
          });
        }

        // Attach event listener for note toggle
        const noteToggle = variantsCell.querySelector('.variants-note-toggle');
        const noteContent = variantsCell.querySelector('.variants-note-content');

        if (noteToggle && noteContent) {
          noteToggle.addEventListener('click', () => {
            const isHidden = noteContent.style.display === 'none';
            noteContent.style.display = isHidden ? 'block' : 'none';
            noteToggle.style.background = isHidden ? '#f3f4f6' : '';
          });
        }
      }
    });
  }

  /**
   * Create table row for document
   * @param {Object} doc - Document object
   * @param {Object} chmed - Parsed CHMED data
   * @returns {HTMLElement}
   */
  createDocumentRow(doc, chmed) {
    const tr = document.createElement('tr');
    tr.dataset.documentId = doc.id;

    // Filename cell - clickable for download
    const filenameCell = document.createElement('td');
    filenameCell.setAttribute('data-label', 'Dateiname');
    filenameCell.innerHTML = `
      <a href="#" class="filename-link" data-id="${doc.id}" title="Klicken zum Download">
        ${doc.filename || doc.id}
      </a>
    `;

    // Make filename clickable for download
    filenameCell.querySelector('.filename-link').addEventListener('click', (e) => {
      e.preventDefault();
      this.downloadDocument(doc.id, doc.filename || 'chmed-document.json');
    });

    // Patient cell
    const patientCell = document.createElement('td');
    patientCell.setAttribute('data-label', 'Patient');
    patientCell.innerHTML = `
      <div class="patient-info">
        <strong>${chmed.patient.lastName}, ${chmed.patient.firstName}</strong>
        <small>*${this.formatDate(chmed.patient.birthDate)}</small>
      </div>
    `;

    // Date cell
    const dateCell = document.createElement('td');
    dateCell.setAttribute('data-label', 'Datum');
    dateCell.textContent = this.formatDate(chmed.date);

    // Medications cell
    const medCell = document.createElement('td');
    medCell.setAttribute('data-label', 'Medikamente');
    const medCount = chmed.medications.length;
    medCell.textContent = medCount;

    // Doctor cell
    const doctorCell = document.createElement('td');
    doctorCell.setAttribute('data-label', 'Arzt');
    doctorCell.textContent = chmed.doctor.name;

    // Actions cell
    const actionsCell = document.createElement('td');
    actionsCell.setAttribute('data-label', 'Aktionen');
    actionsCell.className = 'actions';

    // Add toggle button if variants are available
    const hasVariants = doc.variants && doc.variants.length > 0;
    const toggleButton = hasVariants
      ? `<button class="btn btn--small btn--secondary btn-toggle-variants" data-id="${doc.id}" title="Produkte anzeigen">
          🛒 Produkte (${doc.variants.length})
        </button>`
      : '';

    actionsCell.innerHTML = `
      ${toggleButton}
      <button class="btn btn--small btn--secondary btn-download" data-id="${doc.id}" title="Download">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px; height:16px; margin-right:4px; display:inline-block; vertical-align:middle;">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        Download
      </button>
      <button class="btn btn--small btn--secondary btn-delete" data-id="${doc.id}" title="Löschen">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px; height:16px; margin-right:4px; display:inline-block; vertical-align:middle;">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        Löschen
      </button>
    `;

    // Assemble row
    tr.appendChild(filenameCell);
    tr.appendChild(patientCell);
    tr.appendChild(dateCell);
    tr.appendChild(medCell);
    tr.appendChild(doctorCell);
    tr.appendChild(actionsCell);

    // Event listeners
    if (hasVariants) {
      actionsCell.querySelector('.btn-toggle-variants').addEventListener('click', (e) => {
        this.toggleVariantsVisibility(doc.id, e.target);
      });
    }

    actionsCell.querySelector('.btn-download').addEventListener('click', () => {
      this.downloadDocument(doc.id, doc.filename || 'chmed-document.json');
    });

    actionsCell.querySelector('.btn-delete').addEventListener('click', () => {
      this.deleteDocument(doc.id);
    });

    return tr;
  }

  /**
   * Toggle visibility of variants section
   * @param {string} docId - Document ID
   * @param {HTMLElement} button - Toggle button element
   */
  toggleVariantsVisibility(docId, button) {
    const variantsRow = document.querySelector(`tr.variants-row[data-document-id="${docId}"]`);

    if (!variantsRow) return;

    const isVisible = variantsRow.style.display !== 'none';

    if (isVisible) {
      variantsRow.style.display = 'none';
      button.textContent = button.textContent.replace('▼', '▶').replace('Verbergen', 'anzeigen');
      if (!button.textContent.includes('▶')) {
        button.textContent = '🛒 ' + button.textContent.replace('🛒 ', '').replace('Produkte', 'Produkte');
      }
    } else {
      variantsRow.style.display = '';
      button.textContent = button.textContent.replace('▶', '▼').replace('anzeigen', 'Verbergen');
      if (!button.textContent.includes('▼')) {
        button.textContent = '▼ ' + button.textContent.replace('🛒 ', '').replace('Produkte', 'Produkte verbergen');
      }
    }
  }

  /**
   * Create Shopify thumbnail URL from image URL
   * @param {string} imageUrl - Original Shopify CDN image URL
   * @param {string} size - Size token (e.g., '100x100', '200x200', '400x400')
   * @param {boolean} crop - Whether to crop the image
   * @returns {string} Thumbnail URL
   */
  createThumbnailUrl(imageUrl, size = '100x100', crop = true) {
    if (!imageUrl) return '';

    // Extract base URL, extension, and query string
    const match = imageUrl.match(/^(.+)\.([a-z]+)(\?.*)?$/i);
    if (!match) return imageUrl;

    const [, baseUrl, extension, queryString] = match;
    const cropSuffix = crop ? '_crop_center' : '';

    return `${baseUrl}_${size}${cropSuffix}.${extension}${queryString || ''}`;
  }

  /**
   * Escape HTML for safe display in attributes
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Render variants section with quantity dropdowns and action buttons
   * @param {Array} variants - Array of variant objects from API
   * @param {string} docId - Document ID for action buttons
   * @returns {string} HTML string for variants section
   */
  renderVariantsSection(variants, docId) {
    if (!variants || variants.length === 0) {
      return '<div class="variants-section"><p>Keine Produkte verfügbar</p></div>';
    }

    const variantItems = variants.map(variant => {
      const productTitle = variant.product?.title || 'Unbekannt';
      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const sku = variant.sku || '';
      const price = variant.price || '0.00';
      const description = variant.product?.description || '';

      // Get image URL and create thumbnail
      const imageUrl = variant.image?.url || variant.product?.featuredImage?.url;
      const imageAlt = variant.image?.altText || productTitle;
      const thumbnailUrl = imageUrl ? this.createThumbnailUrl(imageUrl, '100x100', true) : '';

      // Extract numeric variant ID for product link
      let numericVariantId = variant.id;
      if (typeof variant.id === 'string' && variant.id.includes('gid://shopify/ProductVariant/')) {
        numericVariantId = variant.id.split('/').pop();
      }
      const productUrl = `/variants/${numericVariantId}`;

      // Generate quantity options (0-5, default 1)
      const qtyOptions = [];
      for (let i = 0; i <= 5; i++) {
        qtyOptions.push(`<option value="${i}"${i === 1 ? ' selected' : ''}>${i}</option>`);
      }

      // Image HTML - clickable to open product page
      const imageHtml = thumbnailUrl
        ? `<a href="${productUrl}" target="_blank" rel="noopener" title="Produkt anzeigen">
             <img src="${thumbnailUrl}" alt="${imageAlt}" class="variant-thumbnail" loading="lazy" style="cursor:pointer;">
           </a>`
        : '<div class="variant-thumbnail-placeholder">📦</div>';

      // Info bubble with description
      const infoBubble = description
        ? `<span class="info-bubble" title="${this.escapeHtml(description)}">ℹ️</span>`
        : '';

      return `
        <div class="variant-item" data-variant-id="${variant.id}" data-sku="${sku}">
          ${imageHtml}
          <div class="variant-info">
            <strong>${productTitle}${variantTitle ? ` - ${variantTitle}` : ''} ${infoBubble}</strong>
            <small>SKU: ${sku} ${price !== '0.00' ? `• Preis: CHF ${price}` : ''}</small>
          </div>
          <div class="variant-quantity">
            <label for="qty-${variant.id}">Menge:</label>
            <select id="qty-${variant.id}" class="variant-qty-select" data-variant-id="${variant.id}">
              ${qtyOptions.join('')}
            </select>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="variants-section" data-doc-id="${docId}">
        <h4>Bestellbare Produkte (${variants.length})</h4>
        ${variantItems}
        <div class="variants-actions">
          <button class="btn btn--secondary btn-add-to-cart" data-doc-id="${docId}">
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="width:16px; height:16px; fill:currentColor; margin-right:6px;">
              <path d="M15.5,24c-0.827,0-1.5-0.673-1.5-1.5s0.673-1.5,1.5-1.5s1.5,0.673,1.5,1.5S16.327,24,15.5,24z M15.5,22 c-0.276,0-0.5,0.224-0.5,0.5s0.224,0.5,0.5,0.5s0.5-0.224,0.5-0.5S15.776,22,15.5,22z"/>
              <path d="M19.5,24c-0.827,0-1.5-0.673-1.5-1.5s0.673-1.5,1.5-1.5s1.5,0.673,1.5,1.5S20.327,24,19.5,24z M19.5,22 c-0.276,0-0.5,0.224-0.5,0.5s0.224,0.5,0.5,0.5s0.5-0.224,0.5-0.5S19.776,22,19.5,22z"/>
              <path d="M14.86,20c-0.432-0.001-0.813-0.276-0.949-0.684l-1.667-5c-0.084-0.253-0.065-0.524,0.054-0.764 c0.12-0.239,0.325-0.417,0.579-0.501C12.979,13.018,13.086,13,13.194,13h9.092l0.732-2.634C23.078,10.151,23.276,10,23.5,10 c0.045,0,0.09,0.006,0.133,0.018c0.266,0.074,0.422,0.35,0.349,0.616l-0.828,2.98c0,0.006-0.008,0.035-0.018,0.062l-1.553,5.593 C21.462,19.699,21.066,20,20.619,20H14.86z M13.193,14l1.667,5l5.758,0l1.39-5h-8.814H13.193z"/>
              <path d="M0.5,17c-0.132,0-0.262-0.054-0.355-0.148C0.05,16.756-0.001,16.629,0,16.495c0.014-1.215,0.263-2.396,0.74-3.513 c0.55-1.103,2.317-1.753,4.764-2.652l0.634-0.234c0.015-0.01,0.081-0.089,0.131-0.295c0.13-0.536,0.008-1.209-0.063-1.323 C5.138,7.326,4.602,5.754,4.742,4.174C4.658,3.166,4.965,2.182,5.609,1.401c0.655-0.794,1.58-1.286,2.605-1.385 c0.091-0.009,0.182-0.014,0.274-0.016c1.045,0.025,2.006,0.45,2.718,1.196c0.712,0.746,1.09,1.723,1.064,2.751 c-0.002,0.075-0.006,0.15-0.012,0.225c0.141,1.58-0.394,3.152-1.471,4.318c-0.061,0.101-0.182,0.775-0.052,1.309 c0.05,0.207,0.116,0.286,0.135,0.297l0.631,0.232c0.605,0.222,1.17,0.43,1.686,0.633c0.257,0.101,0.383,0.392,0.282,0.648 c-0.076,0.193-0.259,0.317-0.465,0.317l0,0c-0.063,0-0.125-0.012-0.183-0.035c-0.506-0.199-1.065-0.405-1.658-0.622l-0.643-0.237 c-0.258-0.096-0.598-0.346-0.756-0.997c-0.173-0.709-0.087-1.81,0.286-2.221c0.897-0.971,1.337-2.281,1.208-3.593 c-0.003-0.033-0.003-0.065,0-0.098c0.006-0.067,0.01-0.134,0.012-0.202c0.019-0.762-0.26-1.486-0.786-2.038 C9.959,1.333,9.25,1.019,8.488,1C8.445,1.002,8.377,1.006,8.31,1.012C7.551,1.085,6.866,1.449,6.381,2.038 c-0.485,0.588-0.712,1.33-0.639,2.089c0.003,0.033,0.003,0.065,0,0.097c-0.127,1.313,0.313,2.621,1.209,3.59 c0.376,0.414,0.462,1.515,0.29,2.225c-0.159,0.651-0.498,0.901-0.755,0.996L5.85,11.268c-2.062,0.758-3.842,1.412-4.203,2.134 C1.299,14.22,1.087,15.097,1.021,16H10.5c0.276,0,0.5,0.224,0.5,0.5S10.776,17,10.5,17H0.5z"/>
            </svg>
            Zum Warenkorb hinzufügen
          </button>
          <button class="btn btn--primary btn-order-now" data-doc-id="${docId}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="width:16px; height:16px; fill:currentColor; margin-right:6px;">
              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
            </svg>
            Sofort bestellen
          </button>
        </div>
        <div class="variants-note-section">
          <div class="variants-note-toggle" data-doc-id="${docId}" style="display:flex; align-items:center; gap:8px; margin-top:12px; cursor:pointer; padding:8px; border-radius:4px; transition:background 0.2s;">
            <svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 64 64" style="width:16px; height:16px;">
              <defs><style>.cls-1,.cls-2{fill:none;stroke:#000;stroke-width:2px}.cls-1{stroke-miterlimit:10}</style></defs>
              <path class="cls-1" d="M52 33.96V58H8V14h23.7"></path>
              <path class="cls-1" d="M18 47l13-5 24-24-8-8-24 24-5 13zM23 34l8 8"></path>
              <path class="cls-2" d="M47 10l1-1a5.44 5.44 0 0 1 7.82.18A5.52 5.52 0 0 1 56 17l-1 1"></path>
            </svg>
            <span style="font-size:0.9em; color:#666;">Notiz zur Bestellung</span>
          </div>
          <div class="variants-note-content" data-doc-id="${docId}" style="display:none; margin-top:8px;">
            <textarea class="variants-order-note" placeholder="Notiz zur Bestellung..." style="width:100%; height:80px; resize:vertical; border:1px solid #ccc; padding:8px; font-size:0.9em; border-radius:4px;"></textarea>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Add variants to Shopify cart
   * @param {string} docId - Document ID
   */
  async addVariantsToCart(docId) {
    const variantsSection = document.querySelector(`.variants-section[data-doc-id="${docId}"]`);
    if (!variantsSection) return;

    const items = [];
    const variantItems = variantsSection.querySelectorAll('.variant-item');

    variantItems.forEach(item => {
      const variantId = item.dataset.variantId;
      const qtySelect = item.querySelector('.variant-qty-select');
      const quantity = qtySelect ? parseInt(qtySelect.value) : 0;

      if (quantity > 0) {
        // Extract numeric ID from Shopify GID format (gid://shopify/ProductVariant/123456)
        let numericId = variantId;
        if (typeof variantId === 'string' && variantId.includes('gid://shopify/ProductVariant/')) {
          numericId = variantId.split('/').pop();
        }

        items.push({
          id: numericId,
          quantity: quantity
        });
      }
    });

    if (items.length === 0) {
      alert('Bitte wählen Sie mindestens ein Produkt mit Menge > 0 aus.');
      return;
    }

    console.log('Adding to cart:', items);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const result = await response.json();
      console.log('Cart response:', result);

      if (result.status && result.status !== 200) {
        throw new Error('Fehler beim Hinzufügen zum Warenkorb');
      }

      alert(`✅ ${items.length} Produkt(e) wurden zum Warenkorb hinzugefügt!`);

      // Update cart UI - support multiple theme patterns
      await this.updateCartUI();

    } catch (err) {
      console.error('Cart error:', err);
      alert('Fehler beim Hinzufügen zum Warenkorb: ' + err.message);
    }
  }

  /**
   * Update cart UI to reflect new cart state
   * Supports multiple Shopify theme patterns
   */
  async updateCartUI() {
    try {
      // Fetch current cart state
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Method 1: Dispatch standard Shopify events (Dawn, Debut themes)
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        detail: cart,
        bubbles: true
      }));

      // Method 2: Legacy jQuery event (older themes)
      if (window.jQuery) {
        window.jQuery('body').trigger('cart.requestComplete', [cart]);
      }

      // Method 3: Update cart count in DOM directly (common pattern)
      const cartCount = cart.item_count || 0;

      // Update nested bubble structure (your theme pattern)
      const bubbleNumElements = document.querySelectorAll('.cart-link__bubble-num');
      bubbleNumElements.forEach(el => {
        el.textContent = cartCount;
      });

      // Update bubble visibility
      const bubbleElements = document.querySelectorAll('.cart-link__bubble');
      bubbleElements.forEach(el => {
        el.setAttribute('data-cart-count', cartCount);
        if (cartCount > 0) {
          el.classList.add('cart-link__bubble--visible');
          el.classList.remove('hidden');
        } else {
          el.classList.remove('cart-link__bubble--visible');
          el.classList.add('hidden');
        }
      });

      // Find other cart count elements (various selectors used by different themes)
      const cartCountSelectors = [
        '[data-cart-count]',
        '.cart-count',
        '.cart__count',
        '#CartCount',
        '[data-header-cart-count]',
        '.header__cart-count'
      ];

      cartCountSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // Skip if already handled as bubble
          if (el.classList.contains('cart-link__bubble') || el.classList.contains('cart-link__bubble-num')) {
            return;
          }

          el.textContent = cartCount;
          el.setAttribute('data-cart-count', cartCount);

          // Show/hide based on count
          if (cartCount > 0) {
            el.classList.remove('hidden');
            el.style.display = '';
          } else {
            el.classList.add('hidden');
          }
        });
      });

      // Method 4: Update data-count attributes (your theme uses this)
      const cartItemsElements = document.querySelectorAll('.cart__items[data-count]');
      cartItemsElements.forEach(el => {
        el.setAttribute('data-count', cartCount);
      });

      // Method 5: Trigger cart drawer/notification (if exists)
      const cartDrawer = document.querySelector('cart-drawer, [data-cart-drawer]');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.renderContents === 'function') {
        cartNotification.renderContents(cart);
      }

      // Method 6: Trigger theme-specific cart refresh function (if exists)
      if (typeof window.theme !== 'undefined' && typeof window.theme.cart === 'object') {
        if (typeof window.theme.cart.updateCart === 'function') {
          window.theme.cart.updateCart();
        }
        if (typeof window.theme.cart.getCart === 'function') {
          window.theme.cart.getCart();
        }
      }

      console.log('Cart UI updated:', { cartCount, cart });

    } catch (err) {
      console.error('Failed to update cart UI:', err);
    }
  }

  /**
   * Show loading overlay
   * @param {string} message - Loading message
   */
  showLoadingOverlay(message = 'Bestellung wird erstellt...') {
    const overlay = document.createElement('div');
    overlay.className = 'chmed-loading-overlay';
    overlay.id = 'chmed-order-loading';
    overlay.innerHTML = `
      <div class="chmed-loading-overlay__content">
        <div class="chmed-loading-overlay__spinner"></div>
        <div class="chmed-loading-overlay__text">${message}</div>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  /**
   * Hide loading overlay
   */
  hideLoadingOverlay() {
    const overlay = document.getElementById('chmed-order-loading');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Create order from selected variants (immediate order)
   * @param {string} docId - Document ID
   */
  async createOrderFromVariants(docId) {
    const variantsSection = document.querySelector(`.variants-section[data-doc-id="${docId}"]`);
    if (!variantsSection) return;

    const items = [];
    const variantItems = variantsSection.querySelectorAll('.variant-item');

    variantItems.forEach(item => {
      const variantId = item.dataset.variantId;
      const qtySelect = item.querySelector('.variant-qty-select');
      const quantity = qtySelect ? parseInt(qtySelect.value) : 0;

      if (quantity > 0) {
        // Extract numeric ID from Shopify GID format (gid://shopify/ProductVariant/123456)
        let numericId = variantId;
        if (typeof variantId === 'string' && variantId.includes('gid://shopify/ProductVariant/')) {
          numericId = variantId.split('/').pop();
        }

        items.push({
          variant_id: numericId,
          quantity: quantity
        });
      }
    });

    if (items.length === 0) {
      alert('Bitte wählen Sie mindestens ein Produkt mit Menge > 0 aus.');
      return;
    }

    // Get customer ID
    const customerId = this.getCustomerId();
    console.log('Customer ID:', customerId);
    if (!customerId) {
      alert('Fehler: Keine Kunden-ID gefunden. Bitte melden Sie sich an.');
      return;
    }

    // Get default address ID
    const addressId = this.getDefaultAddressId();
    console.log('Default Address ID:', addressId);
    if (!addressId) {
      alert('Fehler: Keine Standard-Adresse gefunden. Bitte fügen Sie eine Adresse hinzu.');
      return;
    }

    // Get hidden fields attributes
    const attribs = this.getHiddenFieldsAttributes();
    console.log('Hidden fields attributes:', attribs);

    // Get order note if provided
    const noteTextarea = variantsSection.querySelector('.variants-order-note');
    const orderNote = noteTextarea ? noteTextarea.value.trim() : '';

    if (orderNote) {
      console.log('Order note:', orderNote);
    }

    // Build payload
    const payload = {
      customerId: customerId,
      items: items,
      attribs: attribs,
      addressId: addressId,
      baseUrl: window.location.origin,
      note: orderNote || ''
    };

    console.log('Creating order from CHMED variants:', payload);

    // Show loading overlay
    this.showLoadingOverlay('Bestellung wird erstellt...');

    try {
      const response = await fetch('/apps/arztfx/createOrder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('Create order result:', result);

      // Hide loading overlay
      this.hideLoadingOverlay();

      if (result.success && result.order && result.order.id) {
        const orderUrl = result.order.order_status_url || `/orders/${result.order.id}`;
        alert(`✅ Bestellung erfolgreich erstellt!\n\nBestellnummer: ${result.order.name}\n\nKlicken Sie OK, um die Bestellung anzuzeigen.`);
        window.open(orderUrl, '_blank');
      } else {
        throw new Error(result.error || 'Fehler beim Erstellen der Bestellung');
      }

    } catch (err) {
      console.error('Create order error:', err);
      this.hideLoadingOverlay();
      alert('Fehler beim Erstellen der Bestellung: ' + err.message);
    }
  }

  /**
   * Download document as PDF file
   * @param {string} docId - Document ID (fileName)
   * @param {string} filename - Suggested filename
   */
  async downloadDocument(docId, filename) {
    try {
      // Use App Proxy endpoint for download
      // fileName is already the document ID in the new API
      const downloadUrl = `${this.apiBaseUrl}/downloadChmed?fileName=${encodeURIComponent(docId)}`;
      
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`Download fehlgeschlagen: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Ensure .pdf extension
      a.download = filename.endsWith('.pdf') ? filename : filename.replace(/\.[^.]+$/, '.pdf');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err) {
  // ...
      alert('Fehler beim Herunterladen: ' + err.message);
    }
  }

  /**
   * Delete document with confirmation
   * @param {string} docId - Document ID (fileName)
   */
  async deleteDocument(docId) {
    if (!confirm('Möchten Sie dieses CHMED-Dokument wirklich löschen?')) {
      return;
    }

    const row = document.querySelector(`tr[data-document-id="${docId}"]`);
    const deleteBtn = row?.querySelector(`.btn-delete[data-id="${docId}"]`);
    
    if (deleteBtn) {
      deleteBtn.disabled = true;
      deleteBtn.textContent = 'Wird gelöscht...';
    }

    try {
      // Use App Proxy endpoint for delete (DELETE method with fileName query param)
      const response = await fetch(`${this.apiBaseUrl}/deleteChmeds?fileName=${encodeURIComponent(docId)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Löschen fehlgeschlagen: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Löschen fehlgeschlagen');
      }

      // Remove row from table
      if (row) {
        row.remove();
      }

      // Check if table is now empty
      const tbody = document.getElementById('chmed-documents-body');
      if (tbody && tbody.children.length === 0) {
        document.getElementById('chmed-table-wrapper').style.display = 'none';
        document.getElementById('chmed-empty').style.display = 'block';
      }

    } catch (err) {
  // ...
      alert('Fehler beim Löschen: ' + err.message);
      
      // Re-enable button
      if (deleteBtn) {
        deleteBtn.disabled = false;
        deleteBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px; height:16px; margin-right:4px; display:inline-block; vertical-align:middle;">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          Löschen
        `;
      }
    }
  }

  /**
   * Show error message
   * @param {string} message - Error message
   */
  showError(message) {
    const loading = document.getElementById('chmed-loading');
    const error = document.getElementById('chmed-error');
    const errorText = document.getElementById('chmed-error-text');

    loading.style.display = 'none';
    error.style.display = 'block';
    
    if (errorText) {
      errorText.textContent = message;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // ...
  const container = document.getElementById('chmed-documents-container');
  if (container) {
  // ...
    new ChmedDocumentManager();
  } else {
  // ...
  }
});
